import { z } from "zod";
import { ProfileLeadStatusSchema } from "./profileLeadStatus";

/**
 * Admin-defined automation: a trigger condition + an action to run when it fires.
 * Discriminated unions on `type` so adding triggers/actions later is additive —
 * no schema rewrite, no graph surgery.
 */

// ── Triggers ────────────────────────────────────────────────────────────────
// v1: status changed on a profile or a lead. Optional from/to allowlists; an
// omitted list means "match any". An omitted list also matches the create case
// (previous status undefined) — a populated `fromStatus` will NOT match create.
export const StatusChangedTriggerSchema = z.object({
	type: z.literal("status_changed"),
	entity: z.enum(["profile", "lead"]),
	fromStatus: z.array(ProfileLeadStatusSchema).optional(),
	toStatus: z.array(ProfileLeadStatusSchema).optional(),
});

export const TriggerSchema = z.discriminatedUnion("type", [
	StatusChangedTriggerSchema,
	// FUTURE: entity_created, task_overdue, scheduled, ...
]);
export type TAutomationTrigger = z.infer<typeof TriggerSchema>;

// ── Actions ─────────────────────────────────────────────────────────────────
// v1: create an admin task. title + description support {{variable}} tokens.
export const CreateTaskActionSchema = z.object({
	type: z.literal("create_task"),
	title: z.string().min(1).max(300),
	description: z.string().max(5000).optional(),
	// v1: generic admin queue only. assignee shape kept for forward-compat.
	assignee: z.object({ type: z.literal("admin") }).default({ type: "admin" }),
	dueInDays: z.number().int().min(0).max(365).optional(),
});

export const ActionSchema = z.discriminatedUnion("type", [
	CreateTaskActionSchema,
	// FUTURE: send_email, set_field, webhook, ...
]);
export type TAutomationAction = z.infer<typeof ActionSchema>;

// ── Rule ──────────────────────────────────────────────────────────────────────
export const AutomationRuleSchema = z.object({
	id: z.string(),
	name: z.string().min(1).max(200),
	enabled: z.boolean(),
	trigger: TriggerSchema,
	action: ActionSchema,
	createdBy: z.enum(["admin"]).default("admin"),
	createdAt: z.number(),
	updatedAt: z.number().optional(),
	// metrics (best-effort, updated by the engine)
	lastFiredAt: z.number().optional(),
	fireCount: z.number().int().nonnegative().optional(),
});
export type TAutomationRule = z.infer<typeof AutomationRuleSchema>;

export const NewAutomationRuleSchema = AutomationRuleSchema.omit({ id: true });
export type TNewAutomationRule = z.infer<typeof NewAutomationRuleSchema>;
